
import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Target, Filter, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { determinaVincitore, formattaData } from './util';
import { LinkGiocatore, LinkPartita, LinkSquadra } from '../../../components/statistiche/Link';
import { slugDaId } from './squadre';
import { urlEdizione } from '../links';
import '../Statistiche.css';

// Il fair play e' un voto e puo' non essere stato assegnato: la partita senza voto mostra un
// trattino, non uno zero, che qui sarebbe il giudizio peggiore invece che un dato mancante.
const voto = (v) => (v === null || v === undefined ? '—' : v);

/**
 * L'elenco delle partite di una edizione, rotta figlia di `EdizioneLayout`.
 *
 * Le statistiche arrivano dall'Outlet. Qui il risparmio è il più grosso dei tre: la risposta
 * porta anche i referti giocatore per giocatore di entrambe le formazioni di ogni partita.
 */
const PartiteView = () => {
  const { statistiche, idEdizione } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSquadra, setFilterSquadra] = useState('');
  const [sortBy, setSortBy] = useState('index');
  const [expandedMatch, setExpandedMatch] = useState(null);

  // Ottieni lista squadre per il filtro
  const squadre = [...new Set([
    ...statistiche.partite.map(p => p.squdra1),
    ...statistiche.partite.map(p => p.squdra2)
  ])].sort();

  // Filtra e ordina le partite
  const partiteFiltrate = statistiche.partite
    .filter(partita => {
      const matchSearch = partita.squdra1.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partita.squdra2.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchSquadra = !filterSquadra || 
                          partita.squdra1 === filterSquadra || 
                          partita.squdra2 === filterSquadra;
      
      return matchSearch && matchSquadra;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'data':
          if (a.data && b.data) {
            return new Date(b.data) - new Date(a.data);
          }
          return 0;
        case 'mete':
          return ((b.mete1 || 0) + (b.mete2 || 0)) - ((a.mete1 || 0) + (a.mete2 || 0));
        case 'scalpi':
          return ((b.pieni1 || 0) + (b.pieni2 || 0) + (b.vuoti1 || 0) + (b.vuoti2 || 0)) - 
                 ((a.pieni1 || 0) + (a.pieni2 || 0) + (a.vuoti1 || 0) + (a.vuoti2 || 0));
        case 'squadra1':
          return a.squdra1.localeCompare(b.squdra1);
        case 'index':
        default:
          return 0;
      }
    });

  const toggleExpanded = (index) => {
    setExpandedMatch(expandedMatch === index ? null : index);
  };

  // I link stanno dentro un'intestazione che apre e chiude il referto: senza fermare l'evento un
  // click lascerebbe la riga aperta alle spalle di chi è già andato altrove.
  const SoloLink = ({ className = '', children }) => (
    <span className={className} onClick={(e) => e.stopPropagation()}>{children}</span>
  );

  const PartitaCompact = ({ partita, index }) => {
    const vincitore = determinaVincitore(partita);
    const isExpanded = expandedMatch === index;
    const fase = partita.fase || 'Fase';
    // Nome gruppo (es. "girone 1") se presente e diverso dalla fase stessa
    const gruppo = partita.nome && partita.nome.toLowerCase() !== fase.toLowerCase() ? partita.nome : null;
    const faseLabel = gruppo ? `${fase} · ${gruppo}` : fase;

    return (
      <div className="partita-compact-card">
        {/* Header compatto cliccabile */}
        <div 
          className="partita-compact-header" 
          onClick={() => toggleExpanded(index)}
        >
          <div className="partita-compact-info">
            {/* La partita ha una pagina sua: il suo numero e la sua fase sono il modo per arrivarci. */}
            <SoloLink>
              <LinkPartita
                className="partita-compact-referto"
                idEdizione={idEdizione}
                idPartita={partita.idPartita}
              >
                <span className="partita-numero-compact">#{index + 1}</span>
                <span className="partita-fase">{faseLabel}</span>
              </LinkPartita>
            </SoloLink>
            <span className="partita-data-compact">
              <Calendar size={14} />
              {formattaData(partita.data)}
            </span>
          </div>
          
          <div className="partita-compact-teams">
            <SoloLink className={`team-name-compact ${vincitore === 1 ? 'winner' : ''}`}>
              <LinkSquadra slug={slugDaId(statistiche, partita.idSquadra1)} nome={partita.squdra1} />
            </SoloLink>
            <span className="vs-compact">vs</span>
            <SoloLink className={`team-name-compact ${vincitore === 2 ? 'winner' : ''}`}>
              <LinkSquadra slug={slugDaId(statistiche, partita.idSquadra2)} nome={partita.squdra2} />
            </SoloLink>
          </div>

          <div className="partita-compact-stats">
            <div className="stat-group">
              <span className="stat-label">Mete</span>
              <span className="stat-values">{partita.mete1 || 0} - {partita.mete2 || 0}</span>
            </div>
            <div className="stat-group">
              <span className="stat-label">Pieni</span>
              <span className="stat-values">{partita.pieni1 || 0} - {partita.pieni2 || 0}</span>
            </div>
            <div className="stat-group">
              <span className="stat-label">Vuoti</span>
              <span className="stat-values">{partita.vuoti1 || 0} - {partita.vuoti2 || 0}</span>
            </div>
            <div className="stat-group">
              <span className="stat-label">Assist</span>
              <span className="stat-values">{partita.assist1 || 0} - {partita.assist2 || 0}</span>
            </div>
            <div className="stat-group">
              <span className="stat-label">Falli</span>
              <span className="stat-values">{partita.falli1 || 0} - {partita.falli2 || 0}</span>
            </div>
            <div className="stat-group">
              <span className="stat-label">Fair play</span>
              <span className="stat-values">{voto(partita.fairPlay1)} - {voto(partita.fairPlay2)}</span>
            </div>
            
          </div>

          <div className="expand-icon">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {/* Dettagli espansi */}
        {isExpanded && (
          <div className="partita-expanded-content">
            <div className="match-result-banner">
              {vincitore === 0 ? (
                <span>🤝 Pareggio</span>
              ) : (
                <span>🏆 Vincitore: {vincitore === 1 ? partita.squdra1 : partita.squdra2}</span>
              )}
            </div>

            {/* Referto giocatori */}
            {(partita.squadra1Giocatori?.length > 0 || partita.squadra2Giocatori?.length > 0) && (
              <div className="players-details">
                <h4>Referto Giocatori</h4>
                
                <div className="teams-details">
                  {/* Squadra 1 */}
                  {partita.squadra1Giocatori?.length > 0 && (
                    <div className="team-detail">
                      <h5 className={vincitore === 1 ? 'team-winner' : ''}>
                        {partita.squdra1} ({partita.squadra1Giocatori.length} giocatori)
                      </h5>
                      <div className="players-table">
                        <div className="player-header">
                          <span>Giocatore</span>
                          <span>Mete</span>
                          <span>Pieni</span>
                          <span>Vuoti</span>
                          <span>Assist</span>
                          <span>Falli</span>
                        </div>
                        {partita.squadra1Giocatori.map((giocatore, idx) => (
                          <div key={`sq1-${idx}`} className="player-row">
                            <span className="player-name-detail">
                              <LinkGiocatore
                                id={giocatore.idGiocatore}
                                nome={giocatore.nome}
                                cognome={giocatore.cognome}
                              />
                            </span>
                            <span className="stat-cell">{giocatore.mete || 0}</span>
                            <span className="stat-cell">{giocatore.pieni || 0}</span>
                            <span className="stat-cell">{giocatore.vuoti || 0}</span>
                            <span className="stat-cell">{giocatore.assist || 0}</span>
                            <span className="stat-cell">{giocatore.falli || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Squadra 2 */}
                  {partita.squadra2Giocatori?.length > 0 && (
                    <div className="team-detail">
                      <h5 className={vincitore === 2 ? 'team-winner' : ''}>
                        {partita.squdra2} ({partita.squadra2Giocatori.length} giocatori)
                      </h5>
                      <div className="players-table">
                        <div className="player-header">
                          <span>Giocatore</span>
                          <span>Mete</span>
                          <span>Pieni</span>
                          <span>Vuoti</span>
                          <span>Assist</span>
                          <span>Falli</span>
                        </div>
                        {partita.squadra2Giocatori.map((giocatore, idx) => (
                          <div key={`sq2-${idx}`} className="player-row">
                            <span className="player-name-detail">
                              <LinkGiocatore
                                id={giocatore.idGiocatore}
                                nome={giocatore.nome}
                                cognome={giocatore.cognome}
                              />
                            </span>
                            <span className="stat-cell">{giocatore.mete || 0}</span>
                            <span className="stat-cell">{giocatore.pieni || 0}</span>
                            <span className="stat-cell">{giocatore.vuoti || 0}</span>
                            <span className="stat-cell">{giocatore.assist || 0}</span>
                            <span className="stat-cell">{giocatore.falli || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="detail-view">
      <div className="detail-header">
        <Link to={urlEdizione(idEdizione)} className="back-button">
          <ArrowLeft size={20} />
          Torna all'edizione
        </Link>
        <div className="detail-title">
          <Target className="detail-icon" />
          <h2>Tutte le Partite ({partiteFiltrate.length})</h2>
        </div>
      </div>

      {/* Filtri e ricerca */}
      <div className="partite-filters">
        <div className="filters-row">
          <select
            value={filterSquadra}
            onChange={(e) => setFilterSquadra(e.target.value)}
            className="filter-select"
          >
            <option value="">Tutte le squadre</option>
            {squadre.map(squadra => (
              <option key={squadra} value={squadra}>{squadra}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista partite compatta */}
      <div className="partite-compact-list">
        {partiteFiltrate.length > 0 ? (
          partiteFiltrate.map((partita, index) => (
            <PartitaCompact 
              key={`${partita.squdra1}-${partita.squdra2}-${index}`}
              partita={partita} 
              index={index}
            />
          ))
        ) : (
          <div className="no-results">
            <Filter className="no-results-icon" />
            <h3>Nessuna partita trovata</h3>
            <p>Prova a modificare i filtri di ricerca</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartiteView;